import React, { useReducer, useContext, useEffect, useRef, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { subcategoryReducer } from './subcategoryReducer';
import {
	ISubcategoryByCustomerV2,
	IGetPaginatedSubcategoriesByCustomerV2,
	ISubcategoryByRestaurantV2,
	IGetPaginatedSubcategoriesByRestaurantV2,
	SortByForPaginatedSubcategories,
	DirectionForPaginated,
} from 'types';
import { PRE_SELECTED_CUSTOMER_ID_FOR_MENU } from 'appConstants';
import { SubcategoryActionTypes } from './subcategoryActions';
import {
	subcategoryInitialState,
	SubcategoryContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './subcategoryContext';
import {
	getPaginatedSubcategoriesByCustomerV2,
	getPaginatedSubcategoriesByRestaurantV2,
	getSubcategoryPhotos,
	deleteSubcategoryV2,
} from 'backend/subcategories';
import { createAppError } from 'utils/helpers';
import { AxiosResponse } from 'axios';
import { useFetchData } from 'hooks/useFetchData';

export const SubcategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(subcategoryReducer, subcategoryInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const {
		refetch: refetchSubcategoriesByCustomer,
		response: responseSubcategoriesByCustomer,
		loading: loadingSubcategoriesByCustomer,
	} = useFetchData<AxiosResponse<ISubcategoryByCustomerV2>, IGetPaginatedSubcategoriesByCustomerV2>(
		isMounted.current && state.IDSelectedCustomer && !state.IDSelectedRestaurant
			? getPaginatedSubcategoriesByCustomerV2
			: null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchInput: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer || '',
			sorting: state.sortBy
				? {
						sortBy: {
							'SubcategoriesA-Z': SortByForPaginatedSubcategories.Name,
							'SubcategoriesZ-A': SortByForPaginatedSubcategories.Name,
						}[state.sortBy],
						direction: {
							'SubcategoriesA-Z': DirectionForPaginated.Ascending,
							'SubcategoriesZ-A': DirectionForPaginated.Descending,
						}[state.sortBy],
				  }
				: null,
		},
	);
	const {
		refetch: refetchSubcategoriesByRestaurant,
		response: responseSubcategoriesByRestaurant,
		loading: loadingSubcategoriesByRestaurant,
	} = useFetchData<AxiosResponse<ISubcategoryByRestaurantV2>, IGetPaginatedSubcategoriesByRestaurantV2>(
		isMounted.current && state.IDSelectedRestaurant ? getPaginatedSubcategoriesByRestaurantV2 : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchInput: state.searchString ? state.searchString : null,
			restaurantId: state.IDSelectedRestaurant || '',
			categoryId: state.IDSelectedCategory,
			isHidden: state.isHidden,
			sorting: state.sortBy
				? {
						sortBy: {
							'SubcategoriesA-Z': SortByForPaginatedSubcategories.Name,
							'SubcategoriesZ-A': SortByForPaginatedSubcategories.Name,
						}[state.sortBy],
						direction: {
							'SubcategoriesA-Z': DirectionForPaginated.Ascending,
							'SubcategoriesZ-A': DirectionForPaginated.Descending,
						}[state.sortBy],
				  }
				: null,
		},
	);

	useEffect(() => {
		loadingSubcategoriesByCustomer || loadingSubcategoriesByRestaurant ? showLoader() : hideLoader();
	}, [loadingSubcategoriesByCustomer, loadingSubcategoriesByRestaurant]);

	useEffect(() => {
		if (responseSubcategoriesByCustomer) {
			dispatch({
				type: SubcategoryActionTypes.SET_SUBCATEGORIES_BY_CUSTOMER,
				payload: {
					subcategoriesByCustomer: responseSubcategoriesByCustomer.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);

			const getSubcategoriesPreSignedUrl = async (subCategoryIds: string[]) => {
				try {
					const subcategoryPhotosResponse = await getSubcategoryPhotos({ subCategoryIds, size: 1 });
					const updatedSubcategoriesByCustomer = responseSubcategoriesByCustomer.data.items.reduce(
						(acc: ISubcategoryByCustomerV2['items'], item) => {
							const subcategoryPhotoFile = subcategoryPhotosResponse.data.files.find(
								(photoFile) => photoFile.id === item.id,
							);
							acc.push({
								...item,
								preSignedUrl: subcategoryPhotoFile ? subcategoryPhotoFile.preSignedUrl : null,
							});
							return acc;
						},
						[],
					);

					dispatch({
						type: SubcategoryActionTypes.SET_SUBCATEGORIES_BY_CUSTOMER,
						payload: {
							subcategoriesByCustomer: {
								totalCount: responseSubcategoriesByCustomer.data.totalCount,
								items: updatedSubcategoriesByCustomer,
							},
							typeOfModalWindow: state.typeOfModalWindow,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const subcategoryIds = responseSubcategoriesByCustomer.data.items.map((subcategory) => subcategory.id);
			getSubcategoriesPreSignedUrl(subcategoryIds);
		}
	}, [responseSubcategoriesByCustomer]);

	useEffect(() => {
		if (responseSubcategoriesByRestaurant) {
			dispatch({
				type: SubcategoryActionTypes.SET_SUBCATEGORIES_BY_RESTAURANT,
				payload: {
					subcategoriesByRestaurant: responseSubcategoriesByRestaurant.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);

			const getSubcategoriesPreSignedUrl = async (subCategoryIds: string[]) => {
				try {
					const subcategoryPhotosResponse = await getSubcategoryPhotos({ subCategoryIds, size: 1 });
					const updatedSubcategoriesByRestaurant = responseSubcategoriesByRestaurant.data.items.reduce(
						(acc: ISubcategoryByRestaurantV2['items'], item) => {
							const subcategoryPhotoFile = subcategoryPhotosResponse.data.files.find(
								(photoFile) => photoFile.id === item.id,
							);
							acc.push({
								...item,
								preSignedUrl: subcategoryPhotoFile ? subcategoryPhotoFile.preSignedUrl : null,
							});
							return acc;
						},
						[],
					);

					dispatch({
						type: SubcategoryActionTypes.SET_SUBCATEGORIES_BY_RESTAURANT,
						payload: {
							subcategoriesByRestaurant: {
								totalCount: responseSubcategoriesByRestaurant.data.totalCount,
								items: updatedSubcategoriesByRestaurant,
							},
							typeOfModalWindow: state.typeOfModalWindow,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const subcategoryIds = responseSubcategoriesByRestaurant.data.items.map((subcategory) => subcategory.id);
			getSubcategoriesPreSignedUrl(subcategoryIds);
		}
	}, [responseSubcategoriesByRestaurant]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetchSubcategoriesByCustomer();
			refetchSubcategoriesByRestaurant();
			dispatch({
				type: SubcategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	const refetchData = (value: boolean) => {
		dispatch({
			type: SubcategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	};

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteSubcategoryV2(state.IDForDeletion);

			if (state.subcategoriesByCustomer.totalCount) {
				const filteredSubcategoriesByCustomer = state.subcategoriesByCustomer.items.filter(
					(el) => el.id !== state.IDForDeletion,
				);
				dispatch({
					type: SubcategoryActionTypes.SET_SUBCATEGORIES_BY_CUSTOMER,
					payload: {
						subcategoriesByCustomer: {
							totalCount: state.subcategoriesByCustomer.totalCount - 1,
							items: filteredSubcategoriesByCustomer,
						},
						typeOfModalWindow: 'success',
					},
				});
			}

			if (state.subcategoriesByRestaurant.totalCount) {
				const filteredSubcategoriesByRestaurant = state.subcategoriesByRestaurant.items.filter(
					(el) => el.id !== state.IDForDeletion,
				);
				dispatch({
					type: SubcategoryActionTypes.SET_SUBCATEGORIES_BY_RESTAURANT,
					payload: {
						subcategoriesByRestaurant: {
							totalCount: state.subcategoriesByRestaurant.totalCount - 1,
							items: filteredSubcategoriesByRestaurant,
						},
						typeOfModalWindow: 'success',
					},
				});
			}
		} catch (err) {
			dispatch({
				type: SubcategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: SubcategoryActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const toggleRememberMyChoice = () => {
		!state.rememberMyChoice && state.IDSelectedCustomer
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, state.IDSelectedCustomer)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: SubcategoryActionTypes.TOGGLE_REMEMBER_MY_CHOICE,
			payload: {
				rememberMyChoice: !state.rememberMyChoice,
			},
		});
	};

	const setIDCustomer = (value: Nullable<string>) => {
		value && state.rememberMyChoice
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: SubcategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value ? value : null,
				subcategoriesByCustomer: value ? state.subcategoriesByCustomer : { totalCount: 0, items: [] },
				subcategoriesByRestaurant: value ? state.subcategoriesByRestaurant : { totalCount: 0, items: [] },
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: SubcategoryActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value ? value : null,
				isHidden: value ? state.isHidden : false,
			},
		});
	};

	const setIDCategory = (value: Nullable<string>) => {
		dispatch({
			type: SubcategoryActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	};

	const toggleIsHidden = () => {
		dispatch({ type: SubcategoryActionTypes.TOGGLE_IS_HIDDEN });
	};

	const resetSelectedControls = () => dispatch({ type: SubcategoryActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: SubcategoryActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: SubcategoryActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: SubcategoryActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: SubcategoryActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForAssignRestaurants = (value: string) => {
		dispatch({
			type: SubcategoryActionTypes.SET_ID_FOR_ASSIGN_RESTAURANTS,
			payload: {
				IDForAssignRestaurants: value,
				typeOfModalWindow: value ? 'assignRestaurants' : null,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: SubcategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		!isMounted.current &&
			value.IDSelectedCustomer &&
			localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value.IDSelectedCustomer);

		dispatch({
			type: SubcategoryActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: {
					...value,
					IDSelectedCustomer:
						value.IDSelectedCustomer || localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU),
				},
			},
		});
		isMounted.current = true;
		refetchSubcategoriesByCustomer();
		refetchSubcategoriesByRestaurant();
	};

	return (
		<SubcategoryContext.Provider
			value={{
				refetchData,
				deleteSelectedItem,
				changeSearchString,
				toggleRememberMyChoice,
				setIDCustomer,
				setIDRestaurant,
				setIDCategory,
				toggleIsHidden,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForAssignRestaurants,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				needRefetchData: state.needRefetchData,
				subcategoriesByCustomer: state.subcategoriesByCustomer,
				subcategoriesByRestaurant: state.subcategoriesByRestaurant,
				searchString: state.searchString,
				rememberMyChoice: state.rememberMyChoice,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedCategory: state.IDSelectedCategory,
				isHidden: state.isHidden,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForAssignRestaurants: state.IDForAssignRestaurants,
				isInitializedSelectCustomerModal: state.isInitializedSelectCustomerModal,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</SubcategoryContext.Provider>
	);
};
